/**
 * Item construction for the context bar's dropdown menus — the PURE part, testable
 * without mounting (same reasoning as context-axis.ts, which see).
 *
 * The two directions of #838 read differently on purpose: the left-to-right cascade
 * keeps FILTERING the lists (an option outside the selected site/program is hidden —
 * a menu of mostly-greyed entries proved unusable in review), while a PIN constrains
 * the other menus by disabling what does not fit and marking what does. `enabledIds`
 * carries that pin constraint; `null` means no pin is active.
 */

export interface ContextMenuItem {
  id: string;
  label: string;
  disabled: boolean;
  /** Compatible with an ACTIVE pin constraint — renders the thumbtack. */
  pinned: boolean;
  /** Why the option is disabled — surfaces as a tooltip. */
  disabledReason?: string;
  /**
   * Stands for a FAMILY rather than one thing — « Tous les programmes », « Groupes »,
   * « Individualisés » (#948). Rendered on a tinted background, because an entry that changes
   * the meaning of every other one should not look like a sibling of them.
   */
  scope?: boolean;
}

export interface ContextMenuSection {
  label?: string;
  items: ContextMenuItem[];
}

export interface BuildMenuItemsOptions<T> {
  id: (entry: T) => string;
  label: (entry: T) => string;
  /** Ids allowed by the active pins. `null` = no pin constraint. */
  enabledIds?: ReadonlySet<string> | null;
  /** Tooltip on entries the pin constraint disables. */
  pinReason?: string;
  /** Marks the family entries of #948 — see {@link ContextMenuItem.scope}. */
  scope?: (entry: T) => boolean;
}

export function buildMenuItems<T>(
  entries: readonly T[],
  options: BuildMenuItemsOptions<T>
): ContextMenuItem[] {
  const enabledIds = options.enabledIds ?? null;
  return entries.map(entry => {
    const id = options.id(entry);
    const fitsPins = enabledIds === null || enabledIds.has(id);
    return {
      id,
      label: options.label(entry),
      disabled: !fitsPins,
      pinned: enabledIds !== null && fitsPins,
      disabledReason: fitsPins ? undefined : options.pinReason,
      scope: options.scope?.(entry) ?? false,
    };
  });
}
